import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { api, setCsrfToken } from './api.js';

// Who is logged in, if anyone. Fetched in the browser after hydration so server-rendered public
// pages are identical for everyone (and cacheable).

export interface SessionInfo {
  role: 'astrologer' | 'admin' | 'partner' | null;
  name?: string;
  csrfToken?: string;
}

interface SessionState {
  session: SessionInfo | null; // null until the first check completes
  refresh: () => Promise<SessionInfo>;
  setSession: (s: SessionInfo) => void;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<SessionInfo | null>(null);

  const setSession = useCallback((s: SessionInfo) => {
    setCsrfToken(s.csrfToken ?? null);
    // A transition, so an answer that arrives while a lazily loaded area (dashboard, admin) is
    // still hydrating on a slow connection doesn't force it to re-render on the client
    // (LANE-D-19: "Suspense boundary received an update before it finished hydrating").
    startTransition(() => {
      setSessionState(s);
    });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const s = await api<SessionInfo>('GET', '/api/v1/session');
      setSession(s);
      return s;
    } catch {
      const s: SessionInfo = { role: null };
      setSession(s);
      return s;
    }
  }, [setSession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <SessionContext.Provider value={{ session, refresh, setSession }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('SessionProvider missing');
  return ctx;
}
